import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InvoicePreviewModal from './InvoicePreviewModal';

const InvoicesTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [previewModal, setPreviewModal] = useState({ isOpen: false, invoice: null });

  const [invoices, setInvoices] = useState([
    {
      id: 1,
      invoiceNumber: 'INV-2026-001',
      clientName: 'Acme Corp',
      plan: 'Enterprise',
      amount: 14999,
      date: '2026-02-05',
      status: 'paid'
    },
    {
      id: 2,
      invoiceNumber: 'INV-2026-002',
      clientName: 'Tech Solutions Ltd',
      plan: 'Scale',
      amount: 5999,
      date: '2026-02-04',
      status: 'paid'
    },
    {
      id: 3,
      invoiceNumber: 'INV-2026-003',
      clientName: 'StartupXYZ',
      plan: 'Growth',
      amount: 2999,
      date: '2026-02-03',
      status: 'pending'
    }
  ]);

  const handleDownloadPDF = (invoice) => {
    console.log('Downloading PDF for:', invoice?.invoiceNumber);
  };

  const handleSendEmail = (invoice) => {
    console.log('Sending email for:', invoice?.invoiceNumber);
  };

  const handlePreview = (invoice) => {
    setPreviewModal({ isOpen: true, invoice });
  };

  const filteredInvoices = invoices?.filter(invoice => {
    const matchesSearch = invoice?.invoiceNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         invoice?.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesSearch;
  });

  const getStatusBadge = (status) => {
    const config = {
      paid: { bg: 'bg-success/10', text: 'text-success', label: 'Paid' },
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending' },
      overdue: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Overdue' }
    };
    const statusConfig = config?.[status] || config?.paid;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        {statusConfig?.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="relative">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            placeholder="Search invoice number or client name..."
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Invoice Number</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices?.map((invoice) => (
              <tr key={invoice?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4 font-mono text-sm font-semibold text-foreground">{invoice?.invoiceNumber}</td>
                <td className="px-4 py-4 font-medium text-foreground">{invoice?.clientName}</td>
                <td className="px-4 py-4 text-foreground">{invoice?.plan}</td>
                <td className="px-4 py-4 text-foreground font-semibold">₹{invoice?.amount?.toLocaleString()}</td>
                <td className="px-4 py-4 text-foreground">{new Date(invoice?.date)?.toLocaleDateString()}</td>
                <td className="px-4 py-4">{getStatusBadge(invoice?.status)}</td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePreview(invoice)}
                      className="p-2 hover:bg-muted rounded-md transition-smooth"
                      title="Preview"
                    >
                      <Icon name="Eye" size={16} className="text-primary" />
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(invoice)}
                      className="p-2 hover:bg-muted rounded-md transition-smooth"
                      title="Download PDF"
                    >
                      <Icon name="Download" size={16} className="text-success" />
                    </button>
                    <button
                      onClick={() => handleSendEmail(invoice)}
                      className="p-2 hover:bg-muted rounded-md transition-smooth"
                      title="Send Email"
                    >
                      <Icon name="Mail" size={16} className="text-info" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredInvoices?.map((invoice) => (
          <div key={invoice?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-mono text-sm font-semibold text-foreground">{invoice?.invoiceNumber}</p>
                <p className="text-sm text-muted-foreground mt-1">{invoice?.clientName}</p>
              </div>
              {getStatusBadge(invoice?.status)}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="text-sm font-medium text-foreground">{invoice?.plan}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="text-sm font-semibold text-foreground">₹{invoice?.amount?.toLocaleString()}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="text-sm font-medium text-foreground">{new Date(invoice?.date)?.toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePreview(invoice)}
                iconName="Eye"
                iconPosition="left"
                fullWidth
              >
                Preview
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleDownloadPDF(invoice)}
                iconName="Download"
                iconPosition="left"
                fullWidth
              >
                Download
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Modal */}
      <InvoicePreviewModal
        isOpen={previewModal?.isOpen}
        invoice={previewModal?.invoice}
        onClose={() => setPreviewModal({ isOpen: false, invoice: null })}
        onDownload={handleDownloadPDF}
        onSendEmail={handleSendEmail}
      />
    </div>
  );
};

export default InvoicesTab;